import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import api from '@/services/api';
import { appelsOffresService, depotsService, analysesService, servicesService, collaborateursService, promptTemplatesService, evaluationsService, criteresService } from '@/services';
import { useToast } from "@/hooks/use-toast";
import Layout from "@/components/Layout";
import WorkflowBar from "@/components/WorkflowBar";
import { ArrowLeft, Edit, FileText, Calendar, User, Target, Settings, BarChart3, Upload, Users, PenTool, Eye, Inbox, TrendingUp, CheckCircle, Search, Shield, Save, X, Loader2, RefreshCcw, Sparkles, Trash2, FileDown, Copy, DollarSign } from "lucide-react";
import { AnalysisViewer } from "@/components/AnalysisViewer";
import { CVViewer } from "@/components/CVViewer";
import { CVImportDialog } from "@/components/CVImportDialog";
import { AOPermissionsDialog } from "@/components/AOPermissionsDialog";
import { useAuth } from "@/hooks/useAuth";
import { useAOPermissions } from "@/hooks/useAOPermissions";
import { convertScoreToFive, cn } from "@/lib/utils";
import { EvaluationsManager } from "@/components/EvaluationsManager";
import { useAOPdfExport } from "@/hooks/useAOPdfExport";
import { AOSocieteNotesManager } from "@/components/AOSocieteNotesManager";
import { AOReportingTab } from "@/components/AOReportingTab";
import { Building2 } from "lucide-react";
interface AppelOffre {
  id: string;
  titre: string;
  reference: string;
  description?: string;
  date_limite: string;
  date_publication?: string;
  date_debut_redaction_ao?: string;
  date_qr_soumissionnaires?: string;
  date_reponses_qr?: string;
  date_analyse_offres?: string;
  date_debut_soutenance?: string;
  date_fin_soutenance?: string;
  date_adjudication?: string;
  date_contractualisation?: string;
  interlocuteur_da?: string;
  ao_manager?: string;
  lien_ao?: string;
  service_responsable_id?: string | null;
  prompt_template_id?: string | null;
  proprietaire_id: string;
  competences_html: string | null;
  certifications_html: string | null;
  poids_competences_pct: number;
  poids_certifications_pct: number;
  seuil_shortlist: number;
  seuil_consider: number;
  gemini_model: string;
  statut?: 'en cours' | 'publié' | 'terminé';
  created_at: string;
  updated_at: string;
}
interface Evaluation {
  id: string;
  ao_id: string;
  nom: string;
  description: string;
  ordre: number;
  ponderation_globale_pct: number;
}
interface CritereEvaluation {
  id: string;
  nom: string;
  description: string;
  ponderation_pct: number;
  contenu_html: string;
  ordre: number;
  evaluation_id: string;
}
interface Service {
  id: string;
  nom: string;
}
interface Collaborateur {
  id: string;
  nom: string;
  service_id?: string | null;
}
interface PromptTemplate {
  id: string;
  name: string;
  type: string;
  template_content: string;
}
interface SocieteNotePdf {
  societe_nom: string;
  note_qualite_offre: number | null;
  note_prix: number | null;
  note_durabilite: number | null;
  note_references: number | null;
  poids_qualite_offre: number | null;
  poids_prix: number | null;
  poids_durabilite: number | null;
  poids_references: number | null;
  score_pondere: number | null;
  commentaire: string | null;
}
interface AppelOffreFormState {
  titre: string;
  reference: string;
  description: string;
  date_limite: string;
  date_publication: string;
  date_debut_redaction_ao: string;
  date_qr_soumissionnaires: string;
  date_reponses_qr: string;
  date_analyse_offres: string;
  date_debut_soutenance: string;
  date_fin_soutenance: string;
  date_adjudication: string;
  date_contractualisation: string;
  interlocuteur_da: string;
  ao_manager: string;
  lien_ao: string;
  service_responsable_id: string;
  prompt_template_id: string;
  statut: 'en cours' | 'publié' | 'terminé' | '';
  seuil_shortlist: number;
  seuil_consider: number;
}
interface Depot {
  id: string;
  candidat_id: string;
  ao_id: string;
  texte_extrait: string | null;
  fichier_file_id: string | null;
  created_at: string;
  extraction_tokens_in: number | null;
  extraction_tokens_out: number | null;
  extraction_cost: number | null;
  extraction_model: string | null;
  candidat: {
    nom: string;
    email: string | null;
    societe: {
      nom: string;
    };
  };
}
interface Stats {
  totalCV: number;
  totalAnalyses: number;
  shortlist: number;
  consider: number;
  reject: number;
  totalTokens: number;
  totalCost: number;
  extractionCost: number;
  extractionTokens: number;
}
interface Analyse {
  id: string;
  depot_id: string;
  ao_id: string;
  resultat_json: any;
  overall_score: number;
  decision: 'shortlist' | 'consider' | 'reject';
  rating?: string | null;
  is_excluded?: boolean;
  comment?: string | null;
  tokens_in: number;
  tokens_out: number;
  cost_estimate: number;
  created_at: string;
  depots: {
    candidats: {
      nom: string;
      societes: {
        nom: string;
      };
    };
    appels_offres: {
      titre: string;
      reference: string;
    };
    texte_extrait: string;
    fichier_file_id: string;
  };
}

type AnalysisMode = 'anonymized_gemini' | 'infomaniak_dual';

const ANALYSIS_MODE_LABELS: Record<AnalysisMode, string> = {
  anonymized_gemini: 'Combo A: Vision + anonymisation Infomaniak -> scoring Gemini 3 Flash',
  infomaniak_dual: 'Combo B: Vision Infomaniak -> scoring dual Kimi K2.5 + Qwen3',
};

const DetailAppelOffres = () => {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    toast
  } = useToast();
  const {
    userRole
  } = useAuth();
  const {
    hasAccess
  } = useAOPermissions();
  const { exportAOToPdf } = useAOPdfExport();
  const isEditMode = location.pathname.endsWith('/edit');
  const [appelOffre, setAppelOffre] = useState<AppelOffre | null>(null);
  const [formState, setFormState] = useState<AppelOffreFormState>({
    titre: '',
    reference: '',
    description: '',
    date_limite: '',
    date_publication: '',
    date_debut_redaction_ao: '',
    date_qr_soumissionnaires: '',
    date_reponses_qr: '',
    date_analyse_offres: '',
    date_debut_soutenance: '',
    date_fin_soutenance: '',
    date_adjudication: '',
    date_contractualisation: '',
    interlocuteur_da: '',
    ao_manager: '',
    lien_ao: '',
    service_responsable_id: '',
    prompt_template_id: '',
    statut: '',
    seuil_shortlist: 0,
    seuil_consider: 0
  });
  const [stats, setStats] = useState<Stats>({
    totalCV: 0,
    totalAnalyses: 0,
    shortlist: 0,
    consider: 0,
    reject: 0,
    totalTokens: 0,
    totalCost: 0,
    extractionCost: 0,
    extractionTokens: 0
  });
  const [evaluations, setEvaluations] = useState<Evaluation[]>([]);
  const [criteresMap, setCriteresMap] = useState<Record<string, CritereEvaluation[]>>({});
  const [editEvaluations, setEditEvaluations] = useState<Evaluation[]>([]);
  const [editableCriteresMap, setEditableCriteresMap] = useState<Record<number, CritereEvaluation[]>>({});
  const [loading, setLoading] = useState(true);
  const [depots, setDepots] = useState<Depot[]>([]);
  const [depotsLoading, setDepotsLoading] = useState(false);
  const [analyses, setAnalyses] = useState<Analyse[]>([]);
  const [analysesLoading, setAnalysesLoading] = useState(false);
  const [selectedAnalysis, setSelectedAnalysis] = useState<Analyse | null>(null);
  const [isAnalysisViewerOpen, setIsAnalysisViewerOpen] = useState(false);
  const [selectedCvData, setSelectedCvData] = useState<{
    candidatNom: string;
    societeNom: string;
    aoTitre: string;
    aoReference: string;
    texteExtrait: string;
    fichierFileId?: string;
    dateCreation?: string;
  } | null>(null);
  const [isCvViewerOpen, setIsCvViewerOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [decisionFilter, setDecisionFilter] = useState<'all' | 'shortlist' | 'consider' | 'reject'>('all');
  const [societeFilter, setSocieteFilter] = useState<string>('all');
  const [societeFilterAnalyses, setSocieteFilterAnalyses] = useState<string>('all');
  const [activeTab, setActiveTab] = useState("overview");
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [services, setServices] = useState<Service[]>([]);
  const [servicesLoaded, setServicesLoaded] = useState(false);
  const [collaborateurs, setCollaborateurs] = useState<Collaborateur[]>([]);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [promptTemplates, setPromptTemplates] = useState<PromptTemplate[]>([]);
  const [promptTemplatesLoaded, setPromptTemplatesLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modelSelectorOpen, setModelSelectorOpen] = useState(false);
  const [pendingAnalysisDepotId, setPendingAnalysisDepotId] = useState<string | null>(null);
  const [pendingAnalysisType, setPendingAnalysisType] = useState<'analyze' | 'reanalyze' | null>(null);
  const [analyzingDepots, setAnalyzingDepots] = useState<string[]>([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [analysisToDelete, setAnalysisToDelete] = useState<string | null>(null);
  const [deleteCvDialogOpen, setDeleteCvDialogOpen] = useState(false);
  const [cvToDelete, setCvToDelete] = useState<{id: string, fichierFileId?: string} | null>(null);
  const [deletingCv, setDeletingCv] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateOptions, setDuplicateOptions] = useState({
    copyDates: false,
    copyContacts: true,
    copyCriteria: true,
    copyThresholds: true,
    copyAnalyses: false
  });
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState("");
  const [savingTitle, setSavingTitle] = useState(false);
  const [societeNotes, setSocieteNotes] = useState<SocieteNotePdf[]>([]);
  const editInitialized = useRef(false);
  const commentTimeouts = useRef<{ [key: string]: NodeJS.Timeout }>({});
  const areCriteresLoaded = useMemo(() => {
    if (evaluations.length === 0) {
      return true;
    }
    return evaluations.every(evaluation => {
      if (!evaluation.id) {
        return true;
      }
      return Object.prototype.hasOwnProperty.call(criteresMap, evaluation.id);
    });
  }, [evaluations, criteresMap]);

  // Initialize edit states when entering edit mode with loaded data
  useEffect(() => {
    if (isEditMode && evaluations.length > 0 && areCriteresLoaded) {
      // Initialize once or when evaluations are loaded while in edit mode
      if (!editInitialized.current || editEvaluations.length === 0) {
        setEditEvaluations(evaluations.map(evaluation => ({
          ...evaluation
        })));
        const editableMap: Record<number, CritereEvaluation[]> = {};
        evaluations.forEach((evaluation, index) => {
          const criteres = evaluation.id ? criteresMap[evaluation.id] || [] : [];
          editableMap[index] = criteres.map(critere => ({
            ...critere
          }));
        });
        setEditableCriteresMap(editableMap);
        editInitialized.current = true;
      }
    } else if (!isEditMode) {
      // Clear edit states when leaving edit mode
      setEditEvaluations([]);
      setEditableCriteresMap({});
      editInitialized.current = false;
    }
  }, [isEditMode, evaluations, criteresMap, areCriteresLoaded]);
  useEffect(() => {
    if (id) {
      checkAccessAndLoad();
    }
  }, [id]);
  useEffect(() => {
    if (id) {
      loadAnalyses();
      loadSocieteNotes();
    }
  }, [id]);
  useEffect(() => {
    if (!appelOffre) return;
    setFormState({
      titre: appelOffre.titre || '',
      reference: appelOffre.reference || '',
      description: appelOffre.description || '',
      date_limite: appelOffre.date_limite || '',
      date_publication: appelOffre.date_publication || '',
      date_debut_redaction_ao: appelOffre.date_debut_redaction_ao || '',
      date_qr_soumissionnaires: appelOffre.date_qr_soumissionnaires || '',
      date_reponses_qr: appelOffre.date_reponses_qr || '',
      date_analyse_offres: appelOffre.date_analyse_offres || '',
      date_debut_soutenance: appelOffre.date_debut_soutenance || '',
      date_fin_soutenance: appelOffre.date_fin_soutenance || '',
      date_adjudication: appelOffre.date_adjudication || '',
      date_contractualisation: appelOffre.date_contractualisation || '',
      interlocuteur_da: appelOffre.interlocuteur_da || '',
      ao_manager: appelOffre.ao_manager || '',
      lien_ao: appelOffre.lien_ao || '',
      service_responsable_id: appelOffre.service_responsable_id || '',
      prompt_template_id: appelOffre.prompt_template_id || '',
      statut: appelOffre.statut || 'en cours',
      seuil_shortlist: appelOffre.seuil_shortlist,
      seuil_consider: appelOffre.seuil_consider
    });
  }, [appelOffre]);
  useEffect(() => {
    loadServices();
    loadPromptTemplates();
    if (isEditMode) {
      loadCollaborateurs();
    }
  }, [isEditMode]);
  const checkAccessAndLoad = async () => {
    // Laisser la RLS décider: on tente de charger les données
    loadAppelOffre();
    loadEvaluationsAndCriteres();
    loadStats();
  };
  useEffect(() => {
    if (activeTab === 'analyses' && id) {
      loadAnalyses();
      loadDepots(); // Needed for cost display in AnalysisViewer
    }
    if (activeTab === 'cv' && id) {
      loadDepots();
    }
  }, [activeTab, id]);
  const loadDepots = async () => {
    if (!id) return;
    console.log('Loading depots for AO:', id);
    setDepotsLoading(true);
    try {
      const data = await depotsService.getByAoId(id);
      console.log('Depots loaded:', data?.length || 0, 'items');

      // Transform the data to match our interface
      const transformedDepots = (data || []).map((depot: any) => ({
        id: depot.id,
        candidat_id: depot.candidatId,
        ao_id: depot.aoId,
        texte_extrait: depot.texteExtrait,
        fichier_file_id: depot.fichierFileId,
        created_at: depot.createdAt,
        extraction_tokens_in: depot.extractionTokensIn || null,
        extraction_tokens_out: depot.extractionTokensOut || null,
        extraction_cost: depot.extractionCost || null,
        extraction_model: depot.extractionModel || null,
        candidat: {
          nom: depot.candidat?.nom || 'Nom inconnu',
          email: depot.candidat?.email || null,
          societe: {
            nom: depot.candidat?.societe?.nom || 'Société inconnue'
          }
        }
      }));
      setDepots(transformedDepots);
    } catch (error) {
      console.error('Erreur lors du chargement des dépôts:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les documents",
        variant: "destructive"
      });
    } finally {
      setDepotsLoading(false);
    }
  };

  const loadSocieteNotes = async () => {
    if (!id) return;
    try {
      // First get society notes via API
      const { data: response } = await api.get(`/appels-offres/${id}/societe-notes`);
      const notesData = response?.data || [];

      // Transform to PDF format with calculated weighted score
      const transformedNotes: SocieteNotePdf[] = notesData.map((note: any) => {
        const poids = {
          qualite: note.poidsQualiteOffre ?? 25,
          prix: note.poidsPrix ?? 25,
          durabilite: note.poidsDurabilite ?? 25,
          references: note.poidsReferences ?? 25
        };

        let score_pondere: number | null = null;
        if (note.noteQualiteOffre !== null || note.notePrix !== null ||
            note.noteDurabilite !== null || note.noteReferences !== null) {
          score_pondere = (
            ((note.noteQualiteOffre || 0) * poids.qualite / 100) +
            ((note.notePrix || 0) * poids.prix / 100) +
            ((note.noteDurabilite || 0) * poids.durabilite / 100) +
            ((note.noteReferences || 0) * poids.references / 100)
          );
          score_pondere = Math.round(score_pondere * 100) / 100;
        }

        return {
          societe_nom: note.societe?.nom || 'Société inconnue',
          note_qualite_offre: note.noteQualiteOffre,
          note_prix: note.notePrix,
          note_durabilite: note.noteDurabilite,
          note_references: note.noteReferences,
          poids_qualite_offre: note.poidsQualiteOffre,
          poids_prix: note.poidsPrix,
          poids_durabilite: note.poidsDurabilite,
          poids_references: note.poidsReferences,
          score_pondere,
          commentaire: note.commentaire
        };
      });

      setSocieteNotes(transformedNotes);
    } catch (error) {
      console.error('Erreur lors du chargement des notes société:', error);
    }
  };

  const handleImportComplete = () => {
    loadDepots();
    loadStats();
  };

  const deleteCvDocument = async (depotId: string, fichierFileId?: string) => {
    try {
      setDeletingCv(true);

      // Supprimer le CV via l'API (qui gère storage + analyses + dépôt)
      await depotsService.delete(depotId);

      toast({
        title: "Document supprimé",
        description: "Le CV et ses analyses associées ont été supprimés avec succès"
      });

      // Recharger les données
      loadDepots();
      loadStats();
      loadAnalyses();
    } catch (error) {
      console.error('Erreur lors de la suppression du CV:', error);
      toast({
        title: "Erreur",
        description: "Impossible de supprimer le document",
        variant: "destructive"
      });
    } finally {
      setDeletingCv(false);
      setDeleteCvDialogOpen(false);
      setCvToDelete(null);
    }
  };

  const openDeleteCvDialog = (depotId: string, fichierFileId?: string) => {
    setCvToDelete({ id: depotId, fichierFileId });
    setDeleteCvDialogOpen(true);
  };
  const loadAnalyses = async () => {
    if (!id) return;
    console.log('Loading analyses for AO:', id);
    setAnalysesLoading(true);
    try {
      const data = await analysesService.getByAoId(id);
      console.log('Analyses loaded:', data?.length || 0, 'items');
      // Transform camelCase to snake_case for compatibility
      setAnalyses((data || []).map((item: any) => ({
        id: item.id,
        depot_id: item.depotId,
        ao_id: item.aoId,
        resultat_json: item.resultatJson,
        overall_score: item.overallScore,
        decision: item.decision as 'shortlist' | 'consider' | 'reject',
        rating: item.rating,
        is_excluded: item.isExcluded,
        comment: item.comment,
        tokens_in: item.tokensIn,
        tokens_out: item.tokensOut,
        cost_estimate: item.costEstimate,
        created_at: item.createdAt,
        depots: {
          candidats: {
            nom: item.depot?.candidat?.nom || 'Inconnu',
            societes: {
              nom: item.depot?.candidat?.societe?.nom || 'Inconnue'
            }
          },
          appels_offres: {
            titre: item.depot?.appelOffre?.titre || '',
            reference: item.depot?.appelOffre?.reference || ''
          },
          texte_extrait: item.depot?.texteExtrait || '',
          fichier_file_id: item.depot?.fichierFileId || ''
        }
      })));
    } catch (error) {
      console.error('Erreur lors du chargement des analyses:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les analyses",
        variant: "destructive"
      });
    } finally {
      setAnalysesLoading(false);
    }
  };
  const loadServices = async () => {
    try {
      const data = await servicesService.getAll();
      setServices(data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des services:', error);
    } finally {
      setServicesLoaded(true);
    }
  };
  const loadCollaborateurs = async () => {
    try {
      const data = await collaborateursService.getAll();
      // Map to expected format
      setCollaborateurs((data || []).map((c: any) => ({
        id: c.id,
        nom: c.nom,
        service_id: c.serviceId
      })));
    } catch (error) {
      console.error('Erreur lors du chargement des collaborateurs:', error);
    }
  };
  const loadPromptTemplates = async () => {
    try {
      const allTemplates = await promptTemplatesService.getAll();
      const data = (allTemplates || []).filter((t: any) => t.type === 'analysis_prompt');
      const mappedData = data.map((t: any) => ({
        id: t.id,
        name: t.name,
        type: t.type,
        template_content: t.templateContent
      }));
      setPromptTemplates(mappedData);
      if (isEditMode && mappedData.length > 0 && !formState.prompt_template_id) {
        const defaultTemplate = mappedData.find((t: any) => t.name === 'Analyse CV Candidats') || mappedData[0];
        setFormState(prev => ({
          ...prev,
          prompt_template_id: defaultTemplate.id
        }));
      }
    } catch (error) {
      console.error('Erreur lors du chargement des templates de prompt:', error);
    } finally {
      setPromptTemplatesLoaded(true);
    }
  };
  const loadAppelOffre = async () => {
    try {
      const data = await appelsOffresService.getById(id!);
      // Map camelCase to snake_case for compatibility
      setAppelOffre({
        id: data.id,
        titre: data.titre,
        reference: data.reference,
        description: data.description,
        date_limite: data.dateLimite,
        date_publication: data.datePublication,
        date_debut_redaction_ao: data.dateDebutRedactionAo,
        date_qr_soumissionnaires: data.dateQrSoumissionnaires,
        date_reponses_qr: data.dateReponsesQr,
        date_analyse_offres: data.dateAnalyseOffres,
        date_debut_soutenance: data.dateDebutSoutenance,
        date_fin_soutenance: data.dateFinSoutenance,
        date_adjudication: data.dateAdjudication,
        date_contractualisation: data.dateContractualisation,
        interlocuteur_da: data.interlocuteurDa,
        ao_manager: data.aoManager,
        lien_ao: data.lienAo,
        service_responsable_id: data.serviceResponsableId,
        prompt_template_id: data.promptTemplateId,
        proprietaire_id: data.proprietaireId,
        competences_html: data.competencesHtml,
        certifications_html: data.certificationsHtml,
        poids_competences_pct: data.poidsCompetencesPct,
        poids_certifications_pct: data.poidsCertificationsPct,
        seuil_shortlist: data.seuilShortlist,
        seuil_consider: data.seuilConsider,
        gemini_model: data.geminiModel,
        statut: data.statut as 'en cours' | 'publié' | 'terminé',
        created_at: data.createdAt,
        updated_at: data.updatedAt
      });
    } catch (error) {
      console.error('Erreur lors du chargement de l\'appel d\'offres:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger l'appel d'offres",
        variant: "destructive"
      });
      navigate('/appels-offres');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickTitleEdit = () => {
    if (appelOffre) {
      setEditedTitle(appelOffre.titre);
      setIsEditingTitle(true);
    }
  };

  const handleSaveQuickTitle = async () => {
    if (!appelOffre || !editedTitle.trim()) return;
    
    setSavingTitle(true);
    try {
      await appelsOffresService.update(appelOffre.id, { titre: editedTitle.trim() });

      setAppelOffre({ ...appelOffre, titre: editedTitle.trim() });
      setIsEditingTitle(false);
      toast({
        title: "Titre mis à jour",
        description: "Le titre de l'appel d'offres a été modifié avec succès"
      });
    } catch (error) {
      console.error('Erreur lors de la mise à jour du titre:', error);
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour le titre",
        variant: "destructive"
      });
    } finally {
      setSavingTitle(false);
    }
  };

  const handleCancelQuickTitleEdit = () => {
    setIsEditingTitle(false);
    setEditedTitle("");
  };
  const loadEvaluationsAndCriteres = async () => {
    try {
      // Charger les évaluations
      const evaluationsData = await evaluationsService.getByAoId(id!);
      // Map to snake_case for compatibility
      const mappedEvaluations = (evaluationsData || []).map((e: any) => ({
        id: e.id,
        ao_id: e.aoId,
        nom: e.nom,
        description: e.description,
        ordre: e.ordre,
        ponderation_globale_pct: e.ponderationGlobalePct
      }));
      setEvaluations(mappedEvaluations);
      if (mappedEvaluations.length > 0) {
        const criteresMapById: Record<string, CritereEvaluation[]> = {};
        const editableMap: Record<number, CritereEvaluation[]> = {};
        let hasCriteriaLoadErrors = false;
        for (const [index, evaluation] of mappedEvaluations.entries()) {
          if (!evaluation.id) continue;
          criteresMapById[evaluation.id] = [];
          try {
            const criteresData = await criteresService.getByEvaluationId(evaluation.id);
            // Map to snake_case
            const criteres = (criteresData || []).map((c: any) => ({
              id: c.id,
              nom: c.nom,
              description: c.description,
              ponderation_pct: c.ponderationPct,
              contenu_html: c.contenuHtml,
              ordre: c.ordre,
              evaluation_id: c.evaluationId
            }));
            console.log('Critères chargés pour l\'évaluation', evaluation.nom, ':', criteres.length, 'critères');
            criteresMapById[evaluation.id] = criteres;
            editableMap[index] = criteres.map(critere => ({
              ...critere
            }));
          } catch (criteresError) {
            hasCriteriaLoadErrors = true;
            console.error('Erreur lors du chargement des critères pour l\'évaluation', evaluation.id, ':', criteresError);
          }
        }
        setCriteresMap(criteresMapById);
        if (hasCriteriaLoadErrors) {
          toast({
            title: "Chargement partiel des critères",
            description: "Certaines évaluations n'ont pas pu charger leurs critères.",
            variant: "destructive"
          });
        }
      } else {
        setCriteresMap({});
      }
    } catch (error) {
      console.error('Erreur lors du chargement des évaluations et critères:', error);
      setEvaluations([]);
      setCriteresMap({});
      toast({
        title: "Erreur",
        description: "Impossible de charger les évaluations et critères de cet appel d'offres.",
        variant: "destructive"
      });
    }
  };
  const loadStats = async () => {
    try {
      // Charger les stats via un endpoint dédié ou calculer côté client
      const depotsData = await depotsService.getByAoId(id!);
      const analysesData = await analysesService.getByAoId(id!);

      const decisions = (analysesData || []).reduce((acc: Record<string, number>, analysis: any) => {
        if (analysis.decision) {
          acc[analysis.decision] = (acc[analysis.decision] || 0) + 1;
        }
        return acc;
      }, {});

      // Calculer totaux tokens et coûts (analyses)
      const totalTokens = (analysesData || []).reduce((sum: number, analysis: any) => {
        return sum + (analysis.tokensIn || 0) + (analysis.tokensOut || 0);
      }, 0);
      const totalCost = (analysesData || []).reduce((sum: number, analysis: any) => {
        return sum + (Number(analysis.costEstimate) || 0);
      }, 0);

      // Calculer totaux extraction (OCR)
      const extractionCost = (depotsData || []).reduce((sum: number, depot: any) => {
        return sum + (Number(depot.extractionCost) || 0);
      }, 0);
      const extractionTokens = (depotsData || []).reduce((sum: number, depot: any) => {
        return sum + (depot.extractionTokensIn || 0) + (depot.extractionTokensOut || 0);
      }, 0);

      setStats({
        totalCV: depotsData?.length || 0,
        totalAnalyses: analysesData?.length || 0,
        shortlist: decisions.shortlist || 0,
        consider: decisions.consider || 0,
        reject: decisions.reject || 0,
        totalTokens: totalTokens,
        totalCost: totalCost,
        extractionCost: extractionCost,
        extractionTokens: extractionTokens
      });
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
    }
  };
  const getServiceDisplayValue = (serviceId?: string | null) => {
    if (!serviceId) return 'Non renseigné';
    const service = services.find(item => item.id === serviceId);
    if (service) return service.nom;
    return servicesLoaded ? 'Service introuvable' : 'Chargement...';
  };
  const getPromptTemplateDisplayValue = (templateId?: string | null) => {
    if (!templateId) return 'Non renseigné';
    const template = promptTemplates.find(item => item.id === templateId);
    if (template) return template.name;
    return promptTemplatesLoaded ? 'Modèle introuvable' : 'Chargement...';
  };
  const getStatusBadge = (statut: 'en cours' | 'publié' | 'terminé' | undefined) => {
    switch (statut) {
      case 'en cours':
        return <Badge className="bg-warning text-warning-foreground">En cours</Badge>;
      case 'publié':
        return <Badge className="bg-success text-success-foreground">Publié</Badge>;
      case 'terminé':
        return <Badge className="bg-destructive text-destructive-foreground">Terminé</Badge>;
      default:
        return <Badge variant="secondary">Inconnu</Badge>;
    }
  };
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };
  const formatHtml = (html: string | null) => {
    if (!html) {
      return <div className="text-sm text-muted-foreground">Aucune information disponible</div>;
    }
    return html.split('\n').map((line, index) => <div key={index} className="mb-1">
        {line.startsWith('•') ? <div className="flex items-start">
            <span className="text-primary mr-2">•</span>
            <span>{line.substring(1).trim()}</span>
          </div> : line}
      </div>);
  };
  const handleUpdateSeuil = async (field: 'seuil_shortlist' | 'seuil_consider', value: number) => {
    if (!appelOffre || !id) return;

    // Vérifier les permissions d'écriture
    if (userRole !== 'admin' && !hasAccess(id, 'write')) {
      toast({
        title: "Accès refusé",
        description: "Vous n'avez pas l'autorisation de modifier cet appel d'offres",
        variant: "destructive"
      });
      return;
    }

    // Validation
    if (field === 'seuil_shortlist' && value <= appelOffre.seuil_consider) {
      toast({
        title: "Erreur de validation",
        description: "Le seuil shortlist doit être supérieur au seuil consider",
        variant: "destructive"
      });
      return;
    }
    if (field === 'seuil_consider' && value >= appelOffre.seuil_shortlist) {
      toast({
        title: "Erreur de validation",
        description: "Le seuil consider doit être inférieur au seuil shortlist",
        variant: "destructive"
      });
      return;
    }
    try {
      // Map field name to camelCase
      const fieldMapping: Record<string, string> = {
        'seuil_shortlist': 'seuilShortlist',
        'seuil_consider': 'seuilConsider'
      };
      const apiField = fieldMapping[field] || field;
      await appelsOffresService.update(id!, { [apiField]: value });
      setAppelOffre({
        ...appelOffre,
        [field]: value
      });
      toast({
        title: "Seuil mis à jour",
        description: "Les modifications ont été sauvegardées"
      });

      // Reload stats to reflect changes
      loadStats();
    } catch (error) {
      console.error('Erreur lors de la mise à jour du seuil:', error);
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour le seuil",
        variant: "destructive"
      });
    }
  };
  const updateFormState = <K extends keyof AppelOffreFormState,>(field: K, value: AppelOffreFormState[K]) => {
    setFormState(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleEditableCriteresChange = (evaluationIndex: number, criteres: CritereEvaluation[]) => {
    setEditableCriteresMap(prev => ({
      ...prev,
      [evaluationIndex]: criteres
    }));
  };
  const handleSave = async () => {
    if (!id) return;
    if (userRole !== 'admin' && !hasAccess(id, 'write')) {
      toast({
        title: "Accès refusé",
        description: "Vous n'avez pas l'autorisation de modifier cet appel d'offres",
        variant: "destructive"
      });
      return;
    }
    if (formState.seuil_shortlist <= formState.seuil_consider) {
      toast({
        title: "Erreur de validation",
        description: "Le seuil shortlist doit être supérieur au seuil consider",
        variant: "destructive"
      });
      return;
    }
    const invalidCriteres = editEvaluations.map((evaluation, index) => {
      const criteres = editableCriteresMap[index] || [];
      const total = criteres.reduce((sum, critere) => sum + (Number(critere.ponderation_pct) || 0), 0);
      return {
        evaluationName: evaluation?.nom?.trim() ? evaluation.nom : `Évaluation ${index + 1}`,
        total,
        hasCriteres: criteres.length > 0
      };
    }).filter(({
      hasCriteres,
      total
    }) => hasCriteres && Math.abs(total - 100) > 0.01);
    if (invalidCriteres.length > 0) {
      const [firstInvalid] = invalidCriteres;
      toast({
        title: "Erreur de validation",
        description: `La somme des pondérations pour ${firstInvalid.evaluationName} doit être égale à 100% (actuellement ${Number(firstInvalid.total.toFixed(2))}%).`,
        variant: "destructive"
      });
      return;
    }
    setSaving(true);
    try {
      const cleanFormData = {
        titre: formState.titre,
        reference: formState.reference,
        description: formState.description || null,
        dateLimite: formState.date_limite || null,
        datePublication: formState.date_publication || null,
        dateDebutRedactionAo: formState.date_debut_redaction_ao || null,
        dateQrSoumissionnaires: formState.date_qr_soumissionnaires || null,
        dateReponsesQr: formState.date_reponses_qr || null,
        dateAnalyseOffres: formState.date_analyse_offres || null,
        dateDebutSoutenance: formState.date_debut_soutenance || null,
        dateFinSoutenance: formState.date_fin_soutenance || null,
        dateAdjudication: formState.date_adjudication || null,
        dateContractualisation: formState.date_contractualisation || null,
        interlocuteurDa: formState.interlocuteur_da || null,
        aoManager: formState.ao_manager || null,
        lienAo: formState.lien_ao || null,
        serviceResponsableId: formState.service_responsable_id || null,
        promptTemplateId: formState.prompt_template_id || null,
        statut: formState.statut || 'en cours',
        seuilShortlist: formState.seuil_shortlist,
        seuilConsider: formState.seuil_consider
      };
      const updatedAo = await appelsOffresService.update(id!, cleanFormData);
      const savedEvaluations: Evaluation[] = [];
      const updatedCriteresMap: Record<string, CritereEvaluation[]> = {};
      if (editEvaluations.length > 0) {
        await evaluationsService.deleteByAoId(id!);
        for (let index = 0; index < editEvaluations.length; index++) {
          const evaluation = editEvaluations[index];
          if (!evaluation.nom) continue;
          const newEvaluation = await evaluationsService.create({
            aoId: id!,
            nom: evaluation.nom,
            description: evaluation.description,
            ordre: index + 1,
            ponderationGlobalePct: evaluation.ponderation_globale_pct
          });
          if (!newEvaluation) {
            console.error('Erreur sauvegarde évaluation');
            continue;
          }
          const normalizedEvaluation: Evaluation = {
            id: newEvaluation.id,
            ao_id: newEvaluation.aoId,
            nom: newEvaluation.nom,
            description: newEvaluation.description,
            ordre: newEvaluation.ordre,
            ponderation_globale_pct: newEvaluation.ponderationGlobalePct
          };
          savedEvaluations.push(normalizedEvaluation);
          const criteres = editableCriteresMap[index] || [];
          if (criteres.length > 0) {
            const insertedCriteres: CritereEvaluation[] = [];
            for (let critereIndex = 0; critereIndex < criteres.length; critereIndex++) {
              const critere = criteres[critereIndex];
              const insertedCritere = await criteresService.create({
                evaluationId: newEvaluation.id,
                nom: critere.nom || `Critère ${critereIndex + 1}`,
                description: critere.description || '',
                ponderationPct: critere.ponderation_pct || 0,
                contenuHtml: critere.contenu_html || '',
                ordre: critereIndex + 1
              });
              if (insertedCritere) {
                insertedCriteres.push({
                  id: insertedCritere.id,
                  nom: insertedCritere.nom,
                  description: insertedCritere.description,
                  ponderation_pct: insertedCritere.ponderationPct,
                  contenu_html: insertedCritere.contenuHtml,
                  ordre: insertedCritere.ordre,
                  evaluation_id: newEvaluation.id
                });
              }
            }
            updatedCriteresMap[newEvaluation.id] = insertedCriteres;
          } else {
            updatedCriteresMap[newEvaluation.id] = [];
          }
        }
      }
      if (updatedAo) {
        setAppelOffre(prev => prev ? {
          ...prev,
          ...updatedAo,
          statut: (updatedAo.statut || 'en cours') as 'en cours' | 'publié' | 'terminé'
        } : prev);
      }
      if (savedEvaluations.length > 0) {
        setEvaluations(savedEvaluations);
        setCriteresMap(updatedCriteresMap);
        setEditEvaluations(savedEvaluations.map(evaluation => ({
          ...evaluation
        })));
        const newEditableMap: Record<number, CritereEvaluation[]> = {};
        savedEvaluations.forEach((evaluation, index) => {
          newEditableMap[index] = updatedCriteresMap[evaluation.id] || [];
        });
        setEditableCriteresMap(newEditableMap);
      } else {
        setEvaluations([]);
        setCriteresMap({});
        setEditEvaluations([]);
        setEditableCriteresMap({});
      }
      toast({
        title: "Modifications enregistrées",
        description: "L'appel d'offres a été mis à jour avec succès"
      });
      navigate(`/appels-offres/${id}`);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast({
        title: "Erreur",
        description: "Impossible d'enregistrer les modifications",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicateAO = async () => {
    if (!appelOffre || !id) return;
    
    setDuplicating(true);
    setDuplicateDialogOpen(false);
    try {
      // Use the API to duplicate the AO
      const { data: response } = await api.post(`/appels-offres/${id}/duplicate`, duplicateOptions);
      const newAO = response?.data;

      if (!newAO) throw new Error('Erreur lors de la duplication');

      toast({
        title: "AO dupliqué",
        description: "L'appel d'offres a été copié avec succès. Vous allez être redirigé."
      });

      // Navigate to the new AO in edit mode
      navigate(`/appels-offres/${newAO.id}/edit`);

    } catch (error) {
      console.error('Erreur lors de la duplication:', error);
      toast({
        title: "Erreur",
        description: "Impossible de dupliquer l'appel d'offres",
        variant: "destructive"
      });
    } finally {
      setDuplicating(false);
    }
  };
  const getWorkflowSteps = () => {
    const steps = [{
      id: 'redaction',
      title: 'Rédaction',
      description: 'Création AO',
      icon: PenTool,
      status: 'completed' as const
    }, {
      id: 'publication',
      title: 'Publication',
      description: 'Diffusion AO',
      icon: Eye,
      status: appelOffre?.date_publication ? 'completed' as const : 'current' as const
    }, {
      id: 'reception',
      title: 'Réception',
      description: 'Collecte CV',
      icon: Inbox,
      status: stats.totalCV > 0 ? 'completed' as const : appelOffre?.date_publication ? 'current' as const : 'upcoming' as const
    }, {
      id: 'analyses',
      title: 'Analyses',
      description: 'Évaluation',
      icon: TrendingUp,
      status: stats.totalAnalyses > 0 ? 'completed' as const : stats.totalCV > 0 ? 'current' as const : 'upcoming' as const
    }, {
      id: 'finalisation',
      title: 'Finalisation',
      description: 'Décision',
      icon: CheckCircle,
      status: appelOffre?.statut === 'terminé' ? 'completed' as const : stats.totalAnalyses > 0 ? 'current' as const : 'upcoming' as const
    }];

    // Calculate current step index
    const currentStepIndex = steps.findIndex(step => step.status === 'current');
    return {
      steps,
      currentStep: currentStepIndex >= 0 ? currentStepIndex : steps.length - 1
    };
  };
  const handleSelectAnalysis = async (analysisId: string) => {
    try {
      const data = await analysesService.getById(analysisId);
      const transformed: Analyse = {
        id: data.id,
        depot_id: data.depotId,
        ao_id: data.aoId,
        resultat_json: data.resultatJson,
        overall_score: data.overallScore || 0,
        decision: (data.decision || 'reject') as 'shortlist' | 'consider' | 'reject',
        rating: data.rating || undefined,
        comment: data.comment || undefined,
        is_excluded: data.isExcluded || false,
        tokens_in: data.tokensIn || 0,
        tokens_out: data.tokensOut || 0,
        cost_estimate: data.costEstimate || 0,
        created_at: data.createdAt,
        depots: {
          candidats: {
            nom: data.depot?.candidat?.nom || 'Inconnu',
            societes: {
              nom: data.depot?.candidat?.societe?.nom || 'Inconnue',
            },
          },
          appels_offres: {
            titre: data.depot?.appelOffre?.titre || '',
            reference: data.depot?.appelOffre?.reference || '',
          },
          texte_extrait: data.depot?.texteExtrait || '',
          fichier_file_id: data.depot?.fichierFileId || '',
        },
      };
      setSelectedAnalysis(transformed);
    } catch (error) {
      console.error('Erreur chargement analyse:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger cette analyse",
        variant: "destructive"
      });
    }
  };

  const handleViewAnalysis = (analysis: Analyse) => {
    console.log('Opening analysis viewer for:', analysis.id);
    setSelectedAnalysis(analysis);
    setIsAnalysisViewerOpen(true);
  };
  const handleViewCV = (analysis: Analyse) => {
    setSelectedCvData({
      candidatNom: analysis.depots.candidats.nom,
      societeNom: analysis.depots.candidats.societes.nom,
      aoTitre: analysis.depots.appels_offres.titre,
      aoReference: analysis.depots.appels_offres.reference,
      texteExtrait: analysis.depots.texte_extrait,
      fichierFileId: analysis.depots.fichier_file_id,
      dateCreation: analysis.created_at
    });
    setIsCvViewerOpen(true);
  };

  const handleDeleteAnalysis = async () => {
    if (!analysisToDelete) return;

    try {
      await analysesService.delete(analysisToDelete);

      toast({
        title: "Analyse supprimée",
        description: "L'analyse a été supprimée avec succès"
      });

      await loadAnalyses();
      await loadStats();
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'analyse:', error);
      toast({
        title: "Erreur",
        description: "Impossible de supprimer l'analyse",
        variant: "destructive"
      });
    } finally {
      setDeleteConfirmOpen(false);
      setAnalysisToDelete(null);
    }
  };

  const openDeleteConfirm = (analysisId: string) => {
    setAnalysisToDelete(analysisId);
    setDeleteConfirmOpen(true);
  };

  const handleRatingChange = async (analysisId: string, rating: string) => {
    try {
      await analysesService.update(analysisId, { rating });

      // Update local state
      setAnalyses(prev =>
        prev.map(a => a.id === analysisId ? { ...a, rating } : a)
      );

      toast({
        title: "Note mise à jour",
        description: `Note ${rating} attribuée`
      });
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la note:', error);
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour la note",
        variant: "destructive"
      });
    }
  };

  const handleExclusionToggle = async (analysisId: string, currentExcluded: boolean) => {
    try {
      const newExcluded = !currentExcluded;
      await analysesService.update(analysisId, { isExcluded: newExcluded });

      // Update local state
      setAnalyses(prev =>
        prev.map(a => a.id === analysisId ? { ...a, is_excluded: newExcluded } : a)
      );

      toast({
        title: newExcluded ? "Candidat exclu" : "Exclusion annulée",
        description: newExcluded ? "Le candidat a été exclu" : "Le candidat n'est plus exclu"
      });
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'exclusion:', error);
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour l'exclusion",
        variant: "destructive"
      });
    }
  };
  
  const handleCommentChange = (analysisId: string, comment: string) => {
    // Update local state immediately for responsive UI
    setAnalyses(prev => prev.map(a => a.id === analysisId ? { ...a, comment } : a));

    // Clear existing timeout for this analysis
    if (commentTimeouts.current[analysisId]) {
      clearTimeout(commentTimeouts.current[analysisId]);
    }

    // Set new timeout to save to database after 500ms of no typing
    commentTimeouts.current[analysisId] = setTimeout(async () => {
      try {
        await analysesService.update(analysisId, { comment });
      } catch (error) {
        console.error('Erreur lors de la mise à jour du commentaire:', error);
        toast({
          title: "Erreur",
          description: "Impossible de mettre à jour le commentaire",
          variant: "destructive"
        });
      }
    }, 500);
  };

  const handleModelSelectorClose = (open: boolean) => {
    setModelSelectorOpen(open);
    if (!open) {
      setPendingAnalysisDepotId(null);
      setPendingAnalysisType(null);
    }
  };
  const analyzeCV = async (
    depotId: string,
    options: {
      mode?: AnalysisMode;
    } = {}
  ) => {
    let alreadyRunning = false;
    setAnalyzingDepots(prev => {
      if (prev.includes(depotId)) {
        alreadyRunning = true;
        return prev;
      }
      return [...prev, depotId];
    });
    if (alreadyRunning) {
      return;
    }
    const mode = options.mode || 'anonymized_gemini';
    try {
      const payload: Record<string, any> = {
        depotId,
        mode,
      };

      const { data: response } = await api.post('/analyses/analyze-cv', payload);

      const analysis = response?.data?.analysis;
      if (!analysis) {
        throw new Error('Analyse échouée');
      }

      const analysisScore = Number(analysis.overallScore || 0);
      const analysisDecision = analysis.decision || 'N/A';
      const modeLabel = ANALYSIS_MODE_LABELS[mode];

      toast({
        title: "Analyse terminée",
        description: `Score: ${analysisScore}/100 (${convertScoreToFive(analysisScore)}/5) - ${analysisDecision} (${modeLabel})`
      });
      await loadAnalyses();
      await loadStats();
    } catch (error) {
      console.error('Erreur analyse:', error);
      const apiError = (error as any)?.response?.data?.error;
      const errorMessage = String(apiError || (error instanceof Error ? error.message : 'Erreur inconnue'));

      if (errorMessage.includes('NO_CRITERIA_CONFIGURED')) {
        toast({
          title: "Configuration requise",
          description: "Aucun critère d'évaluation configuré. Veuillez configurer les critères dans l'onglet Évaluations.",
          variant: "destructive"
        });
      } else if (errorMessage.includes("critère d'évaluation") || errorMessage.includes("critères d'évaluation")) {
        toast({
          title: "Configuration requise",
          description: "Aucun critère d'évaluation configuré. Veuillez configurer les critères dans l'onglet Évaluations.",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Analyse partielle",
          description: errorMessage || "L'analyse a rencontré des erreurs. Essayez de relancer l'analyse.",
          variant: "destructive"
        });
      }
      await loadAnalyses();
      await loadStats();
    } finally {
      setAnalyzingDepots(prev => prev.filter(id => id !== depotId));
    }
  };
  const reanalyzeCVWithMode = async (
    depotId: string,
    options: {
      mode?: AnalysisMode;
    } = {}
  ) => {
    // Keep existing analyses for run history — just create a new one
    await analyzeCV(depotId, options);
  };
  const openModelSelector = (depotId: string, type: 'analyze' | 'reanalyze') => {
    setPendingAnalysisDepotId(depotId);
    setPendingAnalysisType(type);
    setModelSelectorOpen(true);
  };
  const handleModeSelection = async (
    mode: AnalysisMode,
  ) => {
    const depotId = pendingAnalysisDepotId;
    const type = pendingAnalysisType;
    handleModelSelectorClose(false);
    if (!depotId || !type) {
      return;
    }
    if (type === 'reanalyze') {
      await reanalyzeCVWithMode(depotId, {
        mode,
      });
    } else {
      await analyzeCV(depotId, {
        mode,
      });
    }
  };
  const getDepotStatusBadge = (depot: Depot) => {
    if (analyzingDepots.includes(depot.id)) {
      return <Badge className="bg-primary text-primary-foreground animate-pulse">Analyse en cours</Badge>;
    }
    if (analysesByDepot[depot.id]) {
      return <Badge className="bg-success text-success-foreground">Analysé</Badge>;
    }
    return <Badge className="bg-warning text-warning-foreground">Extrait</Badge>;
  };
  const getDecisionBadge = (decision: string, score: number) => {
    const formattedScore = Number(score).toFixed(1);
    const fiveScaleScore = convertScoreToFive(score);
    switch (decision) {
      case 'shortlist':
        return <Badge className="bg-success text-success-foreground">Shortlist ({formattedScore}/100 • {fiveScaleScore}/5)</Badge>;
      case 'consider':
        return <Badge className="bg-warning text-warning-foreground">À considérer ({formattedScore}/100 • {fiveScaleScore}/5)</Badge>;
      case 'reject':
        return <Badge variant="destructive">Rejeté ({formattedScore}/100 • {fiveScaleScore}/5)</Badge>;
      default:
        return <Badge variant="outline">Inconnu ({formattedScore}/100 • {fiveScaleScore}/5)</Badge>;
    }
  };
  const formatAnalysisDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const extractFilename = (fichierFileId: string | null): string => {
    if (!fichierFileId) return 'Nom non détecté';
    // The filename format is: timestamp-originalfilename
    // Extract everything after the first dash
    const parts = fichierFileId.split('-');
    if (parts.length < 2) return fichierFileId;
    return parts.slice(1).join('-');
  };
  const analysesByDepot = useMemo(() => {
    const map: Record<string, Analyse> = {};
    analyses.forEach(analysis => {
      const existing = map[analysis.depot_id];
      if (!existing || new Date(analysis.created_at) > new Date(existing.created_at)) {
        map[analysis.depot_id] = analysis;
      }
    });
    return map;
  }, [analyses]);

  const analysesByDepotAll = useMemo(() => {
    const map: Record<string, Analyse[]> = {};
    analyses.forEach(analysis => {
      if (!map[analysis.depot_id]) map[analysis.depot_id] = [];
      map[analysis.depot_id].push(analysis);
    });
    Object.keys(map).forEach(depotId => {
      map[depotId].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    });
    return map;
  }, [analyses]);

  const depotCosts = useMemo(() => {
    const costs: Record<string, { extraction: number; analysis: number; total: number }> = {};
    depots.forEach(depot => {
      const extraction = Number(depot.extraction_cost) || 0;
      const depotAnalyses = analysesByDepotAll[depot.id] || [];
      const analysis = depotAnalyses.reduce((sum, a) => sum + (Number(a.cost_estimate) || 0), 0);
      costs[depot.id] = { extraction, analysis, total: extraction + analysis };
    });
    return costs;
  }, [depots, analysesByDepotAll]);

  // Extract unique company names from depots
  const uniqueSocietes = useMemo(() => {
    const societes = new Set<string>();
    depots.forEach(depot => {
      if (depot.candidat?.societe?.nom) {
        societes.add(depot.candidat.societe.nom);
      }
    });
    return Array.from(societes).sort();
  }, [depots]);

  const filteredAnalyses = analyses
    .filter(analysis => {
      // Filter by search term
      const matchesSearch = analysis.depots.candidats.nom.toLowerCase().includes(searchTerm.toLowerCase()) || 
        analysis.depots.candidats.societes.nom.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Filter by decision
      const matchesDecision = decisionFilter === 'all' || analysis.decision === decisionFilter;
      
      // Filter by société
      const matchesSociete = societeFilterAnalyses === 'all' || 
        analysis.depots.candidats.societes.nom === societeFilterAnalyses;
      
      return matchesSearch && matchesDecision && matchesSociete;
    })
    .sort((a, b) => {
      // Ordre: A (note/rating), puis B, C, et exclus en dernier
      const getDecisionOrder = (analysis: any) => {
        const decisionRaw = (analysis.decision ?? '').toString().toLowerCase().trim();
        const ratingRaw = (analysis.rating ?? '').toString().trim().toUpperCase();

        // Exclus toujours en dernier
        if (analysis.is_excluded) return 999;
        if (['exclu', 'exclude', 'excluded'].includes(decisionRaw)) return 999;

        // Prioriser la note (rating) si disponible: A < B < C
        if (ratingRaw.startsWith('A')) return 1; // A
        if (ratingRaw.startsWith('B')) return 2; // B
        if (ratingRaw.startsWith('C')) return 3; // C

        // Repli sur le champ decision s'il n'y a pas de note
        if (['shortlist', 'a'].includes(decisionRaw)) return 1; // A
        if (
          ['consider', 'considerer', 'b', 'à considérer', 'a considérer', 'a considerer', 'à considerer'].includes(
            decisionRaw
          )
        )
          return 2; // B
        if (['reject', 'rejete', 'rejet', 'c'].includes(decisionRaw)) return 3; // C

        // Cas sans décision/notation : après C mais avant exclus
        return 4;
      };

      const orderA = getDecisionOrder(a);
      const orderB = getDecisionOrder(b);

      // Si différente catégorie, trier par ordre
      if (orderA !== orderB) {
        return orderA - orderB;
      }

      // Même catégorie: trier par score décroissant (valeurs manquantes -> 0)
      const scoreA = Number(a.overall_score ?? 0);
      const scoreB = Number(b.overall_score ?? 0);
      return scoreB - scoreA;
    });
  if (loading) {
    return <Layout>
        <div className="flex items-center justify-center min-h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Chargement de l'appel d'offres...</p>
          </div>
        </div>
      </Layout>;
  }
  if (!appelOffre) {
    return <Layout>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold mb-4">Appel d'offres introuvable</h2>
          <Button onClick={() => navigate('/appels-offres')}>
            Retour à la liste
          </Button>
        </div>
      </Layout>;
  }
  const canEdit = userRole === 'admin' || hasAccess(appelOffre.id, 'write');
  const displayStatut = isEditMode ? formState.statut as 'en cours' | 'publié' | 'terminé' | undefined : appelOffre.statut;
  return <Layout>
      <div className="w-full space-y-6 flex flex-col h-full">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center space-x-4">
            <Button variant="outline" size="sm" onClick={() => navigate('/appels-offres')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour
            </Button>
            <div>
              <div className="flex items-center space-x-3">
                {isEditingTitle ? (
                  <div className="flex items-center gap-2">
                    <Input
                      value={editedTitle}
                      onChange={(e) => setEditedTitle(e.target.value)}
                      className="text-2xl font-bold h-10 min-w-[300px]"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveQuickTitle();
                        if (e.key === 'Escape') handleCancelQuickTitleEdit();
                      }}
                    />
                    <Button size="sm" onClick={handleSaveQuickTitle} disabled={savingTitle}>
                      {savingTitle ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    </Button>
                    <Button size="sm" variant="outline" onClick={handleCancelQuickTitleEdit} disabled={savingTitle}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 group">
                    <h1 className="text-3xl font-bold text-foreground">
                      {isEditMode ? formState.titre || "Titre de l'appel d'offres" : appelOffre.titre}
                    </h1>
                    {canEdit && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={handleQuickTitleEdit}
                      >
                        <PenTool className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                )}
                {getStatusBadge(displayStatut)}
              </div>
              <p className="text-muted-foreground">
                Réf: {isEditMode ? formState.reference || 'N/A' : appelOffre.reference}
              </p>
            </div>
          </div>
          <div className="flex space-x-2">
            {isEditMode ? <>
                <Button variant="outline" onClick={() => navigate(`/appels-offres/${appelOffre.id}`)} disabled={saving}>
                  <X className="h-4 w-4 mr-2" />
                  Annuler
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Enregistrement...' : 'Enregistrer'}
                </Button>
              </> : <>
                <Button variant="outline" onClick={() => exportAOToPdf(appelOffre, evaluations, criteresMap, stats, services, societeNotes)}>
                  <FileDown className="h-4 w-4 mr-2" />
                  PDF
                </Button>
                <Button variant="outline" onClick={() => setDuplicateDialogOpen(true)} disabled={duplicating}>
                  {duplicating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Copy className="h-4 w-4 mr-2" />}
                  {duplicating ? 'Duplication...' : 'Dupliquer'}
                </Button>
                {userRole === 'admin' && <Button variant="outline" onClick={() => setPermissionsDialogOpen(true)}>
                    <Shield className="h-4 w-4 mr-2" />
                    Accès
                  </Button>}
                {canEdit && <Button onClick={() => navigate(`/appels-offres/${appelOffre.id}/edit`)}>
                    <Edit className="h-4 w-4 mr-2" />
                    Modifier
                  </Button>}
              </>}
          </div>
        </div>

        {/* Workflow Bar */}
        <div className="bg-card border border-card-border rounded-lg px-6">
          <WorkflowBar {...getWorkflowSteps()} />
        </div>

        {/* Stats rapides */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
          <Card className="card-shadow">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium">Documents</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xl font-bold">{stats.totalCV}</div>
              <p className="text-xs text-muted-foreground">Importés</p>
            </CardContent>
          </Card>

          <Card className="card-shadow">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium">Analyses</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xl font-bold text-accent">{stats.totalAnalyses}</div>
              <p className="text-xs text-muted-foreground">Scores générés</p>
            </CardContent>
          </Card>

          <Card className="card-shadow">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium">Shortlist</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xl font-bold text-success">{stats.shortlist}</div>
              <p className="text-xs text-muted-foreground">≥ {isEditMode ? formState.seuil_shortlist : appelOffre.seuil_shortlist} pts</p>
            </CardContent>
          </Card>

          <Card className="card-shadow">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium">À considérer</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xl font-bold text-warning">{stats.consider}</div>
              <p className="text-xs text-muted-foreground">{isEditMode ? formState.seuil_consider : appelOffre.seuil_consider}-{(isEditMode ? formState.seuil_shortlist : appelOffre.seuil_shortlist) - 1} pts</p>
            </CardContent>
          </Card>

          <Card className="card-shadow">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium">Rejetés</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xl font-bold text-destructive">{stats.reject}</div>
              <p className="text-xs text-muted-foreground">&lt; {isEditMode ? formState.seuil_consider : appelOffre.seuil_consider} pts</p>
            </CardContent>
          </Card>

          <Card className="card-shadow">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium">Coût Extraction</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xl font-bold text-orange-500">CHF {stats.extractionCost.toFixed(4)}</div>
              <p className="text-xs text-muted-foreground">{stats.extractionTokens.toLocaleString()} tokens</p>
            </CardContent>
          </Card>

          <Card className="card-shadow">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium">Coût Analyse</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xl font-bold text-primary">CHF {stats.totalCost.toFixed(4)}</div>
              <p className="text-xs text-muted-foreground">{stats.totalTokens.toLocaleString()} tokens</p>
            </CardContent>
          </Card>

          <Card className="card-shadow">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium">Coût Total</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xl font-bold text-primary">CHF {(stats.totalCost + stats.extractionCost).toFixed(4)}</div>
              <p className="text-xs text-muted-foreground">Extraction + Analyse</p>
            </CardContent>
          </Card>
        </div>

        {/* Contenu principal */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="overview">Vue d'ensemble</TabsTrigger>
            <TabsTrigger value="criteria">Critères</TabsTrigger>
            <TabsTrigger value="cv">Dossiers ({stats.totalCV})</TabsTrigger>
            <TabsTrigger value="analyses">Analyses IA ({stats.totalAnalyses})</TabsTrigger>
            <TabsTrigger value="societes">
              <Building2 className="h-4 w-4 mr-1" />
              Analyse Sociétés
            </TabsTrigger>
            <TabsTrigger value="reporting">
              <FileDown className="h-4 w-4 mr-1" />
              Reporting
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="flex-1 overflow-auto mt-6">
            {/* Planning Card - Full Width */}
            <Card className="card-shadow mb-6">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center">
                  <Calendar className="h-5 w-5 mr-2" />
                  Planning
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/planning-ao?view=gantt&year=${new Date(formState.date_limite || Date.now()).getFullYear()}`)}
                  className="gap-1"
                >
                  <BarChart3 className="h-4 w-4" />
                  Voir Gantt
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="space-y-1">
                    <Label className="text-sm font-medium text-orange-600">Début rédaction</Label>
                    {isEditMode ? <Input type="date" value={formState.date_debut_redaction_ao || ''} onChange={e => updateFormState('date_debut_redaction_ao', e.target.value)} /> : <div className="flex items-center text-sm text-muted-foreground mt-1">
                        <Calendar className="h-4 w-4 mr-1" />
                        {formState.date_debut_redaction_ao ? formatDate(formState.date_debut_redaction_ao) : '-'}
                      </div>}
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm font-medium text-orange-600">Date publication AO</Label>
                    {isEditMode ? <Input type="date" value={formState.date_publication || ''} onChange={e => updateFormState('date_publication', e.target.value)} /> : <div className="flex items-center text-sm text-muted-foreground mt-1">
                        <Calendar className="h-4 w-4 mr-1" />
                        {formState.date_publication ? formatDate(formState.date_publication) : '-'}
                      </div>}
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm font-medium text-orange-600">Date limite soumission</Label>
                    {isEditMode ? <Input type="date" value={formState.date_limite || ''} onChange={e => updateFormState('date_limite', e.target.value)} /> : <div className="flex items-center text-sm text-muted-foreground mt-1">
                        <Calendar className="h-4 w-4 mr-1" />
                        {formState.date_limite ? formatDate(formState.date_limite) : '-'}
                      </div>}
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm font-medium text-purple-600">Q&R soumis.</Label>
                    {isEditMode ? <Input type="date" value={formState.date_qr_soumissionnaires || ''} onChange={e => updateFormState('date_qr_soumissionnaires', e.target.value)} /> : <div className="flex items-center text-sm text-muted-foreground mt-1">
                        <Calendar className="h-4 w-4 mr-1" />
                        {formState.date_qr_soumissionnaires ? formatDate(formState.date_qr_soumissionnaires) : '-'}
                      </div>}
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm font-medium text-purple-600">Réponses Q&R</Label>
                    {isEditMode ? <Input type="date" value={formState.date_reponses_qr || ''} onChange={e => updateFormState('date_reponses_qr', e.target.value)} /> : <div className="flex items-center text-sm text-muted-foreground mt-1">
                        <Calendar className="h-4 w-4 mr-1" />
                        {formState.date_reponses_qr ? formatDate(formState.date_reponses_qr) : '-'}
                      </div>}
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="space-y-1">
                    <Label className="text-sm font-medium text-cyan-600">Analyse Offres</Label>
                    {isEditMode ? <Input type="date" value={formState.date_analyse_offres || ''} onChange={e => updateFormState('date_analyse_offres', e.target.value)} /> : <div className="flex items-center text-sm text-muted-foreground mt-1">
                        <Calendar className="h-4 w-4 mr-1" />
                        {formState.date_analyse_offres ? formatDate(formState.date_analyse_offres) : '-'}
                      </div>}
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm font-medium text-pink-600">Début soutenance</Label>
                    {isEditMode ? <Input type="date" value={formState.date_debut_soutenance || ''} onChange={e => updateFormState('date_debut_soutenance', e.target.value)} /> : <div className="flex items-center text-sm text-muted-foreground mt-1">
                        <Calendar className="h-4 w-4 mr-1" />
                        {formState.date_debut_soutenance ? formatDate(formState.date_debut_soutenance) : '-'}
                      </div>}
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm font-medium text-pink-600">Fin soutenance</Label>
                    {isEditMode ? <Input type="date" value={formState.date_fin_soutenance || ''} onChange={e => updateFormState('date_fin_soutenance', e.target.value)} /> : <div className="flex items-center text-sm text-muted-foreground mt-1">
                        <Calendar className="h-4 w-4 mr-1" />
                        {formState.date_fin_soutenance ? formatDate(formState.date_fin_soutenance) : '-'}
                      </div>}
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm font-medium text-emerald-600">Adjudication</Label>
                    {isEditMode ? <Input type="date" value={formState.date_adjudication || ''} onChange={e => updateFormState('date_adjudication', e.target.value)} /> : <div className="flex items-center text-sm text-muted-foreground mt-1">
                        <Calendar className="h-4 w-4 mr-1" />
                        {formState.date_adjudication ? formatDate(formState.date_adjudication) : '-'}
                      </div>}
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm font-medium text-amber-600">Contractualisation</Label>
                    {isEditMode ? <Input type="date" value={formState.date_contractualisation || ''} onChange={e => updateFormState('date_contractualisation', e.target.value)} /> : <div className="flex items-center text-sm text-muted-foreground mt-1">
                        <Calendar className="h-4 w-4 mr-1" />
                        {formState.date_contractualisation ? formatDate(formState.date_contractualisation) : '-'}
                      </div>}
                  </div>
                </div>

                {/* Mini Gantt Preview */}
                {!isEditMode && (() => {
                  const phases = [
                    { label: 'Rédaction', start: formState.date_debut_redaction_ao, end: formState.date_publication, color: 'bg-orange-500' },
                    { label: 'Publication', start: formState.date_publication, end: formState.date_limite, color: 'bg-orange-400' },
                    { label: 'Q&R', start: formState.date_qr_soumissionnaires, end: formState.date_reponses_qr, color: 'bg-purple-500' },
                    { label: 'Analyse', start: formState.date_analyse_offres, end: formState.date_debut_soutenance || formState.date_analyse_offres, color: 'bg-cyan-500' },
                    { label: 'Soutenance', start: formState.date_debut_soutenance, end: formState.date_fin_soutenance, color: 'bg-pink-500' },
                    { label: 'Adjudication', start: formState.date_adjudication, end: formState.date_contractualisation || formState.date_adjudication, color: 'bg-emerald-500' },
                    { label: 'Contrat', start: formState.date_contractualisation, end: formState.date_contractualisation, color: 'bg-amber-500' },
                  ].filter(p => p.start);

                  if (phases.length === 0) return null;

                  const allDates = phases.flatMap(p => [new Date(p.start!), new Date(p.end || p.start!)]);
                  const minDate = new Date(Math.min(...allDates.map(d => d.getTime())));
                  const maxDate = new Date(Math.max(...allDates.map(d => d.getTime())));
                  const totalDays = Math.max(1, (maxDate.getTime() - minDate.getTime()) / (1000 * 60 * 60 * 24));
                  const today = new Date();

                  const getPosition = (date: string) => {
                    const d = new Date(date);
                    return ((d.getTime() - minDate.getTime()) / (1000 * 60 * 60 * 24)) / totalDays * 100;
                  };

                  const todayPosition = ((today.getTime() - minDate.getTime()) / (1000 * 60 * 60 * 24)) / totalDays * 100;

                  return (
                    <div className="mt-6 pt-4 border-t">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-muted-foreground">{formatDate(minDate.toISOString().split('T')[0])}</span>
                        <span className="text-xs font-medium">Timeline</span>
                        <span className="text-xs text-muted-foreground">{formatDate(maxDate.toISOString().split('T')[0])}</span>
                      </div>
                      <div className="relative h-8 bg-muted rounded-lg overflow-hidden">
                        {phases.map((phase, idx) => {
                          const left = getPosition(phase.start!);
                          const endDate = phase.end || phase.start!;
                          const width = Math.max(2, getPosition(endDate) - left);
                          return (
                            <div
                              key={idx}
                              className={`absolute h-full ${phase.color} opacity-80 hover:opacity-100 transition-opacity cursor-default`}
                              style={{ left: `${left}%`, width: `${width}%` }}
                              title={`${phase.label}: ${formatDate(phase.start!)}${phase.end && phase.end !== phase.start ? ` - ${formatDate(phase.end)}` : ''}`}
                            />
                          );
                        })}
                        {todayPosition >= 0 && todayPosition <= 100 && (
                          <div
                            className="absolute top-0 bottom-0 w-0.5 bg-red-600 z-10"
                            style={{ left: `${todayPosition}%` }}
                            title={`Aujourd'hui: ${formatDate(today.toISOString().split('T')[0])}`}
                          />
                        )}
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                        {phases.map((phase, idx) => (
                          <div key={idx} className="flex items-center gap-1">
                            <div className={`w-2 h-2 rounded-full ${phase.color}`} />
                            <span className="text-xs text-muted-foreground">{phase.label}</span>
                          </div>
                        ))}
                        <div className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-red-600" />
                          <span className="text-xs text-muted-foreground">Aujourd'hui</span>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="card-shadow">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <FileText className="h-5 w-5 mr-2" />
                    Informations générales
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Description</Label>
                    {isEditMode ? <Textarea value={formState.description} onChange={e => updateFormState('description', e.target.value)} placeholder="Description détaillée de l'appel d'offres..." rows={4} /> : <p className="text-sm text-muted-foreground mt-1">
                        {appelOffre.description || 'Aucune description fournie'}
                      </p>}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-1">
                      <Label className="text-sm font-medium">Interlocuteur DA</Label>
                      {isEditMode ? <Input value={formState.interlocuteur_da} onChange={e => updateFormState('interlocuteur_da', e.target.value)} placeholder="Contact côté donneur d'ordre" /> : <p className="text-sm text-muted-foreground mt-1">
                          {appelOffre.interlocuteur_da || 'Non renseigné'}
                        </p>}
                    </div>
                    <div className="space-y-1">
                      <Label className="text-sm font-medium">Manager AO</Label>
                      {isEditMode ? <Select value={formState.ao_manager || 'none'} onValueChange={value => {
                          const selectedCollaborateur = collaborateurs.find(c => c.nom === value);
                          updateFormState('ao_manager', value === 'none' ? '' : value);
                          if (selectedCollaborateur?.service_id) {
                            updateFormState('service_responsable_id', selectedCollaborateur.service_id);
                          }
                        }}>
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionner un manager" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Aucun manager</SelectItem>
                            {collaborateurs.map(collaborateur => <SelectItem key={collaborateur.id} value={collaborateur.nom}>
                                {collaborateur.nom}
                              </SelectItem>)}
                          </SelectContent>
                        </Select> : <p className="text-sm text-muted-foreground mt-1">
                          {appelOffre.ao_manager || 'Non renseigné'}
                        </p>}
                    </div>
                    <div className="space-y-1">
                      <Label className="text-sm font-medium">Lien de l'AO</Label>
                      {isEditMode ? <Input type="url" value={formState.lien_ao} onChange={e => updateFormState('lien_ao', e.target.value)} placeholder="https://..." /> : appelOffre.lien_ao ? <a href={appelOffre.lien_ao} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline mt-1 inline-block">
                          Voir l'AO
                        </a> : <p className="text-sm text-muted-foreground mt-1">Non renseigné</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-1">
                      <Label className="text-sm font-medium">Service responsable</Label>
                      {isEditMode ? <Select value={formState.service_responsable_id || 'none'} onValueChange={value => updateFormState('service_responsable_id', value === 'none' ? '' : value)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionner un service" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Aucun service</SelectItem>
                            {services.map(service => <SelectItem key={service.id} value={service.id}>
                                {service.nom}
                              </SelectItem>)}
                          </SelectContent>
                        </Select> : <p className="text-sm text-muted-foreground mt-1">
                          {getServiceDisplayValue(appelOffre.service_responsable_id)}
                        </p>}
                    </div>
                    <div className="space-y-1">
                      <Label className="text-sm font-medium">Type d'analyse</Label>
                      {isEditMode ? <Select value={formState.prompt_template_id || 'none'} onValueChange={value => updateFormState('prompt_template_id', value === 'none' ? '' : value)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionner un template" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Aucun template</SelectItem>
                            {promptTemplates.map(template => <SelectItem key={template.id} value={template.id}>
                                {template.name}
                              </SelectItem>)}
                          </SelectContent>
                        </Select> : <p className="text-sm font-semibold mt-1">
                          {getPromptTemplateDisplayValue(appelOffre.prompt_template_id)}
                        </p>}
                    </div>
                    <div className="space-y-1">
                      <Label className="text-sm font-medium">Statut</Label>
                      {isEditMode ? <Select value={formState.statut || 'en cours'} onValueChange={value => updateFormState('statut', value as 'en cours' | 'publié' | 'terminé')}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="en cours">En cours</SelectItem>
                            <SelectItem value="publié">Publié</SelectItem>
                            <SelectItem value="terminé">Terminé</SelectItem>
                          </SelectContent>
                        </Select> : <div className="mt-1">
                          {getStatusBadge(appelOffre.statut)}
                        </div>}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium">Créé le</Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        {formatDate(appelOffre.created_at)}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Modifié le</Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        {formatDate(appelOffre.updated_at)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-shadow">
                <CardHeader>
                  <CardTitle className="flex items-center">
                   <Settings className="h-5 w-5 mr-2" />
                   Configuration scoring
                 </CardTitle>
               </CardHeader>
               <CardContent className="space-y-4">
                   <div>
                     <Label className="text-sm font-medium">Pondération par évaluation</Label>
                     <div className="mt-2 space-y-3">
                       {evaluations.map(evaluation => <div key={evaluation.id}>
                           <div className="flex justify-between text-sm font-medium mb-1">
                             <span>{evaluation.nom}</span>
                             <span>{evaluation.ponderation_globale_pct}%</span>
                           </div>
                           <Progress value={evaluation.ponderation_globale_pct} className="h-2 mb-2" />
                           {criteresMap[evaluation.id] && criteresMap[evaluation.id].length > 0 && <div className="ml-4 space-y-1">
                               {criteresMap[evaluation.id].map(critere => <div key={critere.id} className="flex justify-between text-xs text-muted-foreground">
                                   <span>• {critere.nom}</span>
                                   <span>{critere.ponderation_pct}%</span>
                                 </div>)}
                             </div>}
                         </div>)}
                     </div>
                   </div>

                   <div>
                     <Label className="text-sm font-medium">Seuils de décision</Label>
                     <div className="mt-2 space-y-4">
                       <div className="space-y-2">
                         <div className="flex items-center justify-between">
                           <Label className="text-sm">Seuil Shortlist</Label>
                           <span className="text-sm font-medium text-success">{isEditMode ? formState.seuil_shortlist : appelOffre.seuil_shortlist} pts</span>
                         </div>
                         {isEditMode ? <Input type="number" min={formState.seuil_consider + 1} max={100} value={formState.seuil_shortlist} onChange={e => updateFormState('seuil_shortlist', Number(e.target.value) || 0)} className="w-full" /> : <Input type="number" min={appelOffre.seuil_consider + 1} max={100} value={appelOffre.seuil_shortlist} onChange={e => handleUpdateSeuil('seuil_shortlist', parseInt(e.target.value) || 0)} className="w-full" />}
                       </div>

                       <div className="space-y-2">
                         <div className="flex items-center justify-between">
                           <Label className="text-sm">Seuil Consider</Label>
                           <span className="text-sm font-medium text-warning">{isEditMode ? formState.seuil_consider : appelOffre.seuil_consider} pts</span>
                         </div>
                         {isEditMode ? <Input type="number" min={0} max={formState.seuil_shortlist - 1} value={formState.seuil_consider} onChange={e => updateFormState('seuil_consider', Number(e.target.value) || 0)} className="w-full" /> : <Input type="number" min={0} max={appelOffre.seuil_shortlist - 1} value={appelOffre.seuil_consider} onChange={e => handleUpdateSeuil('seuil_consider', parseInt(e.target.value) || 0)} className="w-full" />}
                       </div>

                       <div className="text-xs text-muted-foreground space-y-1">
                         <div className="flex justify-between">
                           <span>Shortlist:</span>
                           <span className="text-success">≥ {isEditMode ? formState.seuil_shortlist : appelOffre.seuil_shortlist} pts</span>
                         </div>
                         <div className="flex justify-between">
                           <span>Consider:</span>
                           <span className="text-warning">{isEditMode ? formState.seuil_consider : appelOffre.seuil_consider}-{(isEditMode ? formState.seuil_shortlist : appelOffre.seuil_shortlist) - 1} pts</span>
                         </div>
                         <div className="flex justify-between">
                           <span>Reject:</span>
                           <span className="text-destructive">&lt; {isEditMode ? formState.seuil_consider : appelOffre.seuil_consider} pts</span>
                         </div>
                       </div>
                     </div>
                   </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="criteria" className="flex-1 overflow-auto mt-6">
            <div className="space-y-6">
              {isEditMode ? <EvaluationsManager aoId={appelOffre.id} initialEvaluations={editEvaluations} initialCriteresMap={editableCriteresMap} onEvaluationsChange={evals => setEditEvaluations(evals as Evaluation[])} onCriteresChange={handleEditableCriteresChange} /> : evaluations.length > 0 ? <div className="space-y-6">
                  {evaluations.map(evaluation => <Card key={evaluation.id} className="card-shadow">
                      <CardHeader>
                        <CardTitle className="flex items-center">
                          <Target className="h-5 w-5 mr-2" />
                          {evaluation.nom}
                          <Badge className="ml-2 bg-primary text-primary-foreground">
                            {evaluation.ponderation_globale_pct}% du total
                          </Badge>
                        </CardTitle>
                        {evaluation.description && <CardDescription>{evaluation.description}</CardDescription>}
                      </CardHeader>
                      <CardContent>
                        {criteresMap[evaluation.id] && criteresMap[evaluation.id].length > 0 ? <div className="space-y-4">
                            <h4 className="text-sm font-medium text-muted-foreground">
                              Critères de cette évaluation ({criteresMap[evaluation.id].length}):
                            </h4>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                              {criteresMap[evaluation.id].map(critere => <Card key={critere.id} className="border border-border/60">
                                  <CardHeader className="pb-3">
                                    <CardTitle className="text-base flex items-center">
                                      <span className="w-2 h-2 bg-primary rounded-full mr-2"></span>
                                      {critere.nom}
                                      <Badge variant="outline" className="ml-2">
                                        {critere.ponderation_pct}%
                                      </Badge>
                                    </CardTitle>
                                    {critere.description && <CardDescription className="text-xs">{critere.description}</CardDescription>}
                                  </CardHeader>
                                  <CardContent className="pt-0">
                                    <div className="text-sm space-y-1">
                                      {formatHtml(critere.contenu_html)}
                                    </div>
                                  </CardContent>
                                </Card>)}
                            </div>
                          </div> : <div className="text-center py-6 border border-dashed rounded-md">
                            <Target className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                            <p className="text-sm text-muted-foreground mb-2">Aucun critère défini pour cette évaluation</p>
                            <Button size="sm" variant="outline" onClick={() => navigate(`/appels-offres/${appelOffre?.id}/edit`)}>
                              Ajouter des critères
                            </Button>
                          </div>}
                      </CardContent>
                    </Card>)}
                </div> : <Card className="card-shadow">
                  <CardContent className="text-center py-8">
                    <Target className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">Aucune évaluation définie</h3>
                    <p className="text-muted-foreground mb-4">
                      Aucune évaluation n'a été configurée pour cet appel d'offres
                    </p>
                    <Button onClick={() => navigate(`/appels-offres/${appelOffre.id}/edit`)}>
                      Configurer les évaluations
                    </Button>
                  </CardContent>
                </Card>}
            </div>
          </TabsContent>

          <TabsContent value="cv" className="flex-1 overflow-auto mt-6">
            <Card className="card-shadow">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Users className="h-5 w-5 mr-2" />
                  Documents importés ({stats.totalCV})
                </CardTitle>
                <CardDescription>
                  Liste des candidatures reçues pour cet appel d'offres
                </CardDescription>
              </CardHeader>
              <CardContent>
                {stats.totalCV === 0 ? <div className="text-center py-8">
                    <Upload className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">Aucun document importé</h3>
                    <p className="text-muted-foreground mb-4">
                      Commencez par importer des documents pour cet appel d'offres
                    </p>
                    <Button onClick={() => setIsImportDialogOpen(true)}>
                      <Upload className="h-4 w-4 mr-2" />
                      Importer des documents
                    </Button>
                  </div> : <>
                      <div className="flex justify-between items-center mb-4 gap-4">
                        <div className="flex items-center gap-2 flex-1">
                          <div className="relative">
                            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                            <Input placeholder="Rechercher par nom ou société..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-8 w-64" />
                          </div>
                          <Select value={societeFilter} onValueChange={setSocieteFilter}>
                            <SelectTrigger className="w-[200px]">
                              <SelectValue placeholder="Toutes les sociétés" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Toutes les sociétés</SelectItem>
                              {uniqueSocietes.map(societe => (
                                <SelectItem key={societe} value={societe}>
                                  {societe}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <Button onClick={() => setIsImportDialogOpen(true)}>
                          <Upload className="h-4 w-4 mr-2" />
                          Importer des documents
                        </Button>
                      </div>

                      {depotsLoading ? <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                        </div> : <div className="space-y-3">
                          {depots.filter(depot => {
                            const matchesSearch = depot.candidat.nom.toLowerCase().includes(searchTerm.toLowerCase()) || depot.candidat.societe.nom.toLowerCase().includes(searchTerm.toLowerCase());
                            const matchesSociete = societeFilter === 'all' || depot.candidat.societe.nom === societeFilter;
                            return matchesSearch && matchesSociete;
                          }).map(depot => {
                    const isAnalyzing = analyzingDepots.includes(depot.id);
                    const hasAnalysis = Boolean(analysesByDepot[depot.id]);
                    return <div key={depot.id} className="flex items-center justify-between p-4 border border-card-border rounded-lg hover:bg-muted/50 transition-smooth">
                                  <div className="flex-1">
                                    <div className="flex items-center space-x-3">
                                      <User className="h-5 w-5 text-primary" />
                                      <div>
                                        <h4 className="font-semibold">{extractFilename(depot.fichier_file_id)}</h4>
                                        <p className="text-sm text-muted-foreground">
                                          {depot.candidat.societe.nom}
                                        </p>
                                        {depot.candidat.email && <p className="text-xs text-muted-foreground">
                                            {depot.candidat.email}
                                          </p>}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center space-x-6">
                                    <div className="text-right">
                                      <div className="text-sm text-muted-foreground">
                                        Importé le {formatAnalysisDate(depot.created_at)}
                                      </div>
                                      {depot.fichier_file_id && <div className="text-xs text-success">
                                          Document disponible
                                        </div>}
                                      {depot.extraction_model && <span className="text-xs text-muted-foreground">({depot.extraction_model.split('/').pop()})</span>}
                                      {(depotCosts[depot.id]?.total ?? 0) > 0 && (
                                        <div className="text-xs mt-1 flex flex-col items-end gap-0.5">
                                          <span className="text-primary font-medium">
                                            <DollarSign className="h-3 w-3 inline mr-0.5" />
                                            Total: CHF {depotCosts[depot.id].total.toFixed(4)}
                                          </span>
                                          <span className="text-muted-foreground">
                                            Extraction: CHF {depotCosts[depot.id].extraction.toFixed(4)} • Analyse: CHF {depotCosts[depot.id].analysis.toFixed(4)}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex flex-col items-end space-y-2">
                                      {getDepotStatusBadge(depot)}
                                      <div className="flex items-center space-x-2">
                                         <Button variant="outline" size="sm" onClick={() => {
                              if (depot.candidat && appelOffre) {
                                setSelectedCvData({
                                  candidatNom: depot.candidat.nom,
                                  societeNom: depot.candidat.societe.nom,
                                  aoTitre: appelOffre.titre,
                                  aoReference: appelOffre.reference,
                                  texteExtrait: depot.texte_extrait || '',
                                  fichierFileId: depot.fichier_file_id || undefined,
                                  dateCreation: depot.created_at
                                });
                                setIsCvViewerOpen(true);
                              }
                            }}>
                                          <Eye className="h-4 w-4 mr-2" />
                                          Voir CV
                                        </Button>
                                        {hasAnalysis && (
                                          <Button variant="outline" size="sm" onClick={() => {
                                            const analysis = analysesByDepot[depot.id];
                                            if (analysis) {
                                              handleViewAnalysis(analysis);
                                            }
                                          }}>
                                            <BarChart3 className="h-4 w-4 mr-2" />
                                            Voir l'analyse
                                          </Button>
                                        )}
                                        <Button variant={hasAnalysis ? 'secondary' : 'default'} size="sm" onClick={() => openModelSelector(depot.id, hasAnalysis ? 'reanalyze' : 'analyze')} disabled={isAnalyzing}>
                                          {isAnalyzing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : hasAnalysis ? <RefreshCcw className="h-4 w-4 mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
                                          {isAnalyzing ? 'Analyse...' : hasAnalysis ? 'Relancer' : 'Analyser'}
                                        </Button>
                                        <Button 
                                          variant="outline" 
                                          size="sm" 
                                          onClick={() => openDeleteCvDialog(depot.id, depot.fichier_file_id || undefined)}
                                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                </div>;
                  })}
                          
                          {depots.filter(depot => {
                            const matchesSearch = depot.candidat.nom.toLowerCase().includes(searchTerm.toLowerCase()) || depot.candidat.societe.nom.toLowerCase().includes(searchTerm.toLowerCase());
                            const matchesSociete = societeFilter === 'all' || depot.candidat.societe.nom === societeFilter;
                            return matchesSearch && matchesSociete;
                          }).length === 0 && <div className="text-center py-8">
                              <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                              <h3 className="text-lg font-semibold mb-2">Aucun résultat</h3>
                              <p className="text-muted-foreground">
                                Aucun document ne correspond à votre recherche
                              </p>
                            </div>}
                        </div>}
                    </>}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="analyses" className="flex-1 overflow-auto mt-6">
            <Card className="card-shadow">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <BarChart3 className="h-5 w-5 mr-2" />
                  Analyses IA ({stats.totalAnalyses})
                </CardTitle>
                <CardDescription>
                  Résultats de l'évaluation automatisée des candidatures
                </CardDescription>
              </CardHeader>
              <CardContent>
                {stats.totalAnalyses === 0 ? <div className="text-center py-8">
                    <BarChart3 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">Aucune analyse disponible</h3>
                    <p className="text-muted-foreground mb-4">
                      Les analyses seront disponibles après l'import et le traitement des documents
                    </p>
                    <Button variant="outline" onClick={() => setIsImportDialogOpen(true)}>
                      <Upload className="h-4 w-4 mr-2" />
                      Importer des documents
                    </Button>
                  </div> : <div className="space-y-4">
                    {/* Barre de recherche et filtre société */}
                    <div className="flex gap-4">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input placeholder="Rechercher par candidat ou société..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" />
                      </div>
                      <Select value={societeFilterAnalyses} onValueChange={setSocieteFilterAnalyses}>
                        <SelectTrigger className="w-[200px]">
                          <SelectValue placeholder="Toutes les sociétés" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Toutes les sociétés</SelectItem>
                          {uniqueSocietes.map(societe => (
                            <SelectItem key={societe} value={societe}>{societe}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Filtres de décision */}
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        variant={decisionFilter === 'all' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setDecisionFilter('all')}
                        className={decisionFilter === 'all' ? 'bg-primary text-primary-foreground' : ''}
                      >
                        Tous ({analyses.length})
                      </Button>
                      <Button
                        variant={decisionFilter === 'shortlist' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setDecisionFilter('shortlist')}
                        className={decisionFilter === 'shortlist' ? 'bg-success text-success-foreground hover:bg-success/90' : ''}
                      >
                        Shortlist ({analyses.filter(a => a.decision === 'shortlist').length})
                      </Button>
                      <Button
                        variant={decisionFilter === 'consider' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setDecisionFilter('consider')}
                        className={decisionFilter === 'consider' ? 'bg-warning text-warning-foreground hover:bg-warning/90' : ''}
                      >
                        À considérer ({analyses.filter(a => a.decision === 'consider').length})
                      </Button>
                      <Button
                        variant={decisionFilter === 'reject' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setDecisionFilter('reject')}
                        className={decisionFilter === 'reject' ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : ''}
                      >
                        Rejetés ({analyses.filter(a => a.decision === 'reject').length})
                      </Button>
                    </div>

                    {analysesLoading ? <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                      </div> : filteredAnalyses.length === 0 ? <div className="text-center py-8">
                        <p className="text-muted-foreground">Aucune analyse trouvée</p>
                      </div> : <div className="space-y-3">
                        {filteredAnalyses.map(analysis => <Card key={analysis.id} className={`border transition-colors ${analysis.is_excluded ? 'border-destructive/50 bg-destructive/5' : 'border-border hover:border-primary/50'}`}>
                            <CardContent className="p-4">
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                   <div className="flex items-center space-x-3 mb-2">
                                     <h3 className="font-medium">{extractFilename(analysis.depots.fichier_file_id)}</h3>
                                     <span className="text-sm text-muted-foreground">
                                       {analysis.depots.candidats.societes.nom}
                                     </span>
                                     {getDecisionBadge(analysis.decision, analysis.overall_score)}
                                     
                                     {/* Rating Selector */}
                                     <Select
                                       value={analysis.rating || ""}
                                       onValueChange={(value) => handleRatingChange(analysis.id, value)}
                                     >
                                       <SelectTrigger className="w-[100px] h-8 bg-background border-border">
                                         <SelectValue placeholder="Note" />
                                       </SelectTrigger>
                                       <SelectContent className="bg-background border-border z-50">
                                         <SelectItem value="A">A</SelectItem>
                                         <SelectItem value="B">B</SelectItem>
                                         <SelectItem value="C">C</SelectItem>
                                       </SelectContent>
                                     </Select>

                                     {/* Exclusion Button */}
                                     <Button
                                       variant={analysis.is_excluded ? "destructive" : "outline"}
                                       size="icon"
                                       className="h-8 w-8"
                                       onClick={() => handleExclusionToggle(analysis.id, analysis.is_excluded || false)}
                                     >
                                       <X className="h-4 w-4" />
                                     </Button>
                                   </div>
                                   
                                   {/* Comment Field */}
                                    <div className="mt-2">
                                      <Textarea
                                        placeholder="Commentaire (max 400 caractères)"
                                        value={analysis.comment || ""}
                                        onChange={(e) => handleCommentChange(analysis.id, e.target.value)}
                                        maxLength={400}
                                        className={cn(
                                          "text-xs resize-none",
                                          analysis.rating === "A" && "bg-green-100 dark:bg-green-950 border-green-300 dark:border-green-800",
                                          analysis.rating === "B" && "bg-yellow-100 dark:bg-yellow-950 border-yellow-300 dark:border-yellow-800",
                                          analysis.rating === "C" && "bg-red-100 dark:bg-red-950 border-red-300 dark:border-red-800"
                                        )}
                                        rows={2}
                                      />
                                    </div>
                                   
                                  <div className="flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
                                    <span>Analysé le {formatAnalysisDate(analysis.created_at)}</span>
                                    {analysis.resultat_json?.analysis_metadata?.model_used && (
                                      <Badge variant="outline" className="text-xs px-2 py-0">
                                        {analysis.resultat_json.analysis_metadata.model_used}
                                      </Badge>
                                    )}
                                    {(analysis.cost_estimate ?? 0) > 0 && (
                                      <span className="flex items-center gap-1">
                                        <DollarSign className="h-3 w-3" />
                                        CHF {analysis.cost_estimate.toFixed(4)}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <Button variant="outline" size="sm" onClick={() => handleViewCV(analysis)}>
                                    <Eye className="h-4 w-4 mr-2" />
                                    CV
                                  </Button>
                                  <Button size="sm" onClick={() => handleViewAnalysis(analysis)}>
                                    <BarChart3 className="h-4 w-4 mr-2" />
                                    Voir l'analyse
                                  </Button>
                                  <Button variant="outline" size="sm" onClick={() => openDeleteConfirm(analysis.id)}>
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  </Button>
                                </div>
                              </div>
                            </CardContent>
                          </Card>)}
                      </div>}
                  </div>}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="societes" className="flex-1 overflow-auto mt-6">
            {id && <AOSocieteNotesManager aoId={id} />}
          </TabsContent>

          <TabsContent value="reporting" className="flex-1 overflow-auto mt-6">
            <AOReportingTab
              appelOffre={appelOffre}
              evaluations={evaluations}
              criteresMap={criteresMap}
              stats={stats}
              services={services}
              societeNotes={societeNotes}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Analysis Viewer */}
      <AnalysisViewer
        isOpen={isAnalysisViewerOpen}
        onClose={() => setIsAnalysisViewerOpen(false)}
        analysis={selectedAnalysis}
        onViewCV={handleViewCV}
        onSelectAnalysis={handleSelectAnalysis}
        onUpdateAnalysis={() => { loadAnalyses(); loadStats(); }}
        depotExtractionCost={selectedAnalysis ? (depots.find(d => d.id === selectedAnalysis.depot_id)?.extraction_cost ?? null) : null}
        isFirstRun={selectedAnalysis ? (() => {
          const depotAnalyses = analysesByDepotAll[selectedAnalysis.depot_id] || [];
          const first = depotAnalyses[0];
          return first?.id === selectedAnalysis.id;
        })() : false}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer cette analyse ? Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={handleDeleteAnalysis}>
              Supprimer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Model selector dialog */}
      <Dialog open={modelSelectorOpen} onOpenChange={handleModelSelectorClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Choisir le mode d'analyse</DialogTitle>
            <DialogDescription>
              Sélectionnez le combo d'analyse à appliquer à cette candidature.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Card className="cursor-pointer hover:bg-accent/50 transition-colors" onClick={() => handleModeSelection('anonymized_gemini')}>
              <CardContent className="p-4">
                <div className="flex items-center space-x-3">
                  <div className="h-3 w-3 rounded-full bg-primary"></div>
                  <div className="flex-1">
                    <h3 className="font-medium">Combo A: Infomaniak + Gemini 3 Flash</h3>
                    <p className="text-sm text-muted-foreground">Extraction Vision et anonymisation via Infomaniak, puis scoring avec Gemini 3 Flash.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:bg-accent/50 transition-colors" onClick={() => handleModeSelection('infomaniak_dual')}>
              <CardContent className="p-4">
                <div className="flex items-center space-x-3">
                  <div className="h-3 w-3 rounded-full bg-emerald-500"></div>
                  <div className="flex-1">
                    <h3 className="font-medium">Combo B: Dual Infomaniak (Kimi K2.5 + Qwen3)</h3>
                    <p className="text-sm text-muted-foreground">Extraction Vision Infomaniak, puis scoring parallèle Kimi K2.5 + Qwen3 avec consensus.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" onClick={() => handleModelSelectorClose(false)}>
              Annuler
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* CV Viewer */}
      <CVViewer isOpen={isCvViewerOpen} onClose={() => setIsCvViewerOpen(false)} cvData={selectedCvData} />

      {/* Permissions Dialog */}
      <AOPermissionsDialog open={permissionsDialogOpen} onOpenChange={setPermissionsDialogOpen} aoId={appelOffre.id} aoTitle={appelOffre.titre} />

      <CVImportDialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen} appelOffreId={appelOffre.id} onImportComplete={handleImportComplete} />

      {/* Dialog de confirmation de suppression de CV */}
      <AlertDialog open={deleteCvDialogOpen} onOpenChange={setDeleteCvDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression du document</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer ce document CV ? Cette action supprimera le fichier et toutes les analyses associées. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => cvToDelete && deleteCvDocument(cvToDelete.id, cvToDelete.fichierFileId)} 
              disabled={deletingCv}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletingCv ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-destructive-foreground mr-2" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Supprimer le document
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog de duplication avec options */}
      <Dialog open={duplicateDialogOpen} onOpenChange={setDuplicateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="h-5 w-5" />
              Dupliquer l'appel d'offres
            </DialogTitle>
            <DialogDescription>
              Sélectionnez les éléments à copier dans le nouvel appel d'offres.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="copyCriteria"
                  checked={duplicateOptions.copyCriteria}
                  onChange={(e) => setDuplicateOptions(prev => ({ ...prev, copyCriteria: e.target.checked }))}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <label htmlFor="copyCriteria" className="text-sm font-medium leading-none">
                  Critères d'évaluation
                  <span className="block text-xs text-muted-foreground font-normal mt-0.5">
                    {evaluations.length} évaluation(s), {Object.values(criteresMap).flat().length} critère(s)
                  </span>
                </label>
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="copyThresholds"
                  checked={duplicateOptions.copyThresholds}
                  onChange={(e) => setDuplicateOptions(prev => ({ ...prev, copyThresholds: e.target.checked }))}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <label htmlFor="copyThresholds" className="text-sm font-medium leading-none">
                  Seuils de décision
                  <span className="block text-xs text-muted-foreground font-normal mt-0.5">
                    Shortlist: {appelOffre.seuil_shortlist}%, À considérer: {appelOffre.seuil_consider}%
                  </span>
                </label>
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="copyContacts"
                  checked={duplicateOptions.copyContacts}
                  onChange={(e) => setDuplicateOptions(prev => ({ ...prev, copyContacts: e.target.checked }))}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <label htmlFor="copyContacts" className="text-sm font-medium leading-none">
                  Contacts et responsabilités
                  <span className="block text-xs text-muted-foreground font-normal mt-0.5">
                    Manager AO, Interlocuteur DA, Service responsable
                  </span>
                </label>
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="copyDates"
                  checked={duplicateOptions.copyDates}
                  onChange={(e) => setDuplicateOptions(prev => ({ ...prev, copyDates: e.target.checked }))}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <label htmlFor="copyDates" className="text-sm font-medium leading-none">
                  Dates du planning
                  <span className="block text-xs text-muted-foreground font-normal mt-0.5">
                    Toutes les dates seront copiées (non recommandé)
                  </span>
                </label>
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="copyAnalyses"
                  checked={duplicateOptions.copyAnalyses}
                  onChange={(e) => setDuplicateOptions(prev => ({ ...prev, copyAnalyses: e.target.checked }))}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <label htmlFor="copyAnalyses" className="text-sm font-medium leading-none">
                  Analyses de CV
                  <span className="block text-xs text-muted-foreground font-normal mt-0.5">
                    {analyses.length} analyse(s) existante(s) seront copiées avec leurs dépôts
                  </span>
                </label>
              </div>
            </div>

            <div className="pt-2 border-t">
              <p className="text-xs text-muted-foreground">
                <strong>Toujours copiés :</strong> Titre (+ "copie"), Référence, Description, Lien AO, Template d'analyse
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDuplicateDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleDuplicateAO} disabled={duplicating}>
              {duplicating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Copy className="h-4 w-4 mr-2" />}
              Dupliquer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>;
};
export default DetailAppelOffres;
